import { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import {
  Plus,
  RefreshCw,
  Unlink,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ExternalLink,
  Shield,
  AlertTriangle,
} from 'lucide-react';

import { PageContainer } from '@/shared/components/layout/PageContainer';
import { LoadingSpinner } from '@/shared/components/feedback/LoadingSpinner';
import { AppButton } from '@/shared/components/ui/AppButton';
import { AppCard } from '@/shared/components/ui/AppCard';
import { AppBadge } from '@/shared/components/ui/AppBadge';
import { AppDialog } from '@/shared/components/ui/AppDialog';
import { AppSelect } from '@/shared/components/ui/AppSelect';
import { PlatformLogo, isPlatformLogoAvailable } from '@/shared/components/promote/PlatformLogo';
import { AdPlatform } from '@/shared/types/enums';
import type { AdAccount } from '@/shared/types/entities';
import { env } from '@/shared/config/env';
import { needsReconnect, hasMissingScopes } from '@/shared/utils/account-health';
import { FEATURE_FLAGS } from '@/shared/config/feature-flags';
import { useFeatureFlag } from '@/shared/hooks/use-feature-flag';

import { useAccounts } from '../../api/accounts-queries';
import { useDisconnectAccount, useSyncAccount } from '../../api/accounts-mutations';
import { useOAuthConnect } from '../../hooks/use-oauth-connect';
import { useOAuthReturn } from '../../hooks/use-oauth-return';
import { platformToPathSegment } from '../../logic/platform-path';
import styles from './AccountsPage.module.scss';

const PLATFORM_OPTIONS = [
  { value: AdPlatform.META, label: 'Meta (Facebook & Instagram)' },
  { value: AdPlatform.GOOGLE, label: 'Google Ads' },
  { value: AdPlatform.TWITTER, label: 'X (Twitter)' },
  ...(env.VITE_ENABLE_TIKTOK_CONNECT ? [{ value: AdPlatform.TIKTOK, label: 'TikTok Ads' }] : []),
];

const PLATFORM_CONFIG: Record<string, { label: string; color: string }> = {
  [AdPlatform.META]: { label: 'Meta', color: 'var(--platform-meta)' },
  [AdPlatform.GOOGLE]: { label: 'Google', color: 'var(--platform-google)' },
  [AdPlatform.TWITTER]: { label: 'X', color: 'var(--platform-twitter)' },
  [AdPlatform.TIKTOK]: { label: 'TikTok', color: 'var(--platform-tiktok)' },
};

const DEFAULT_STATUS = { variant: 'warning' as const, label: 'Unknown' };

const STATUS_MAP: Record<string, { variant: 'success' | 'error' | 'warning'; label: string }> = {
  active: { variant: 'success', label: 'Connected' },
  disconnected: { variant: 'error', label: 'Disconnected' },
  error: { variant: 'warning', label: 'Error' },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(dateStr));
}

const cardVariants = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: { delay: i * 0.06, duration: 0.25 },
  }),
  exit: { opacity: 0, scale: 0.96, transition: { duration: 0.15 } },
};

export function AccountsPage() {
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [accountToDisconnect, setAccountToDisconnect] = useState<AdAccount | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  const xPlatformEnabled = useFeatureFlag(FEATURE_FLAGS.X_PLATFORM);

  const platformOptions = useMemo(
    () =>
      PLATFORM_OPTIONS.filter((option) => option.value !== AdPlatform.TWITTER || xPlatformEnabled),
    [xPlatformEnabled],
  );

  const { data: accounts, isLoading, isError } = useAccounts();
  const disconnectMutation = useDisconnectAccount();
  const syncMutation = useSyncAccount();
  const { connectingPlatform, startConnect, cancelConnect } = useOAuthConnect();
  useOAuthReturn();

  const isConnecting = connectingPlatform !== null;
  const connectingLabel = connectingPlatform
    ? (PLATFORM_CONFIG[connectingPlatform]?.label ?? 'platform')
    : 'platform';

  const closeConnectDialog = useCallback(() => {
    if (isConnecting) return;
    setConnectDialogOpen(false);
    setSelectedPlatform('');
  }, [isConnecting]);

  const handleConnectAccount = useCallback(() => {
    if (!selectedPlatform) return;
    startConnect(selectedPlatform);
  }, [selectedPlatform, startConnect]);

  // The OAuth tab resolves into a toast; drop the dialog once the hold state clears.
  useEffect(() => {
    if (!isConnecting) return;
    return () => {
      setConnectDialogOpen(false);
      setSelectedPlatform('');
    };
  }, [isConnecting]);

  const handleConfirmDisconnect = useCallback(() => {
    if (!accountToDisconnect) return;
    setDisconnectingId(accountToDisconnect.id);
    disconnectMutation.mutate(
      {
        adAccountId: accountToDisconnect.id,
        platform: platformToPathSegment(accountToDisconnect.platform),
      },
      {
        onSettled: () => {
          setDisconnectingId(null);
          setAccountToDisconnect(null);
        },
      },
    );
  }, [accountToDisconnect, disconnectMutation]);

  const handleSync = useCallback(
    (account: AdAccount) => {
      syncMutation.mutate(account.id);
    },
    [syncMutation],
  );

  return (
    <PageContainer
      title="Ad Accounts"
      subtitle="Connect and manage your advertising accounts"
      actions={
        <AppButton onClick={() => setConnectDialogOpen(true)}>
          <Plus size={16} />
          Connect Account
        </AppButton>
      }
    >
      <div className={styles.container}>
        {isLoading && (
          <div className={styles.grid}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={styles.skeleton} style={{ height: 180 }} />
            ))}
          </div>
        )}

        {isError && (
          <div className={styles.errorState}>
            <p>Failed to load accounts. Please try again.</p>
          </div>
        )}

        {!isLoading && !isError && accounts && accounts.length === 0 && (
          <motion.div
            className={styles.emptyState}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.emptyIcon}>
              <ExternalLink size={48} />
            </div>
            <h3 className={styles.emptyTitle}>No accounts connected</h3>
            <p className={styles.emptyDescription}>
              Connect your Meta, Google, or X ad account to start running campaigns.
            </p>
            <AppButton onClick={() => setConnectDialogOpen(true)}>
              <Plus size={16} />
              Connect Account
            </AppButton>
          </motion.div>
        )}

        {!isLoading && !isError && accounts && accounts.length > 0 && (
          <div className={styles.grid}>
            <AnimatePresence mode="popLayout">
              {accounts.map((account, index) => {
                const platformConfig = PLATFORM_CONFIG[account.platform];
                const statusConfig = STATUS_MAP[account.status] ?? DEFAULT_STATUS;
                const isSyncing = syncMutation.isPending && syncMutation.variables === account.id;
                const isDisconnecting = disconnectingId === account.id;
                const isManaged = account.isManaged === true;
                // A dead or dying credential is only fixable by re-running
                // OAuth, so the card has to say so and offer the action —
                // otherwise the failure only ever surfaces deeper in the app,
                // as a generic "try again" on whatever call hit the platform.
                const mustReconnect = needsReconnect(account);
                // A valid-but-under-scoped token is a different problem from a
                // dying one: nothing is broken yet, and the fix unlocks a
                // feature rather than restoring delivery.
                const missingPermission = hasMissingScopes(account);
                const isReconnecting = connectingPlatform === account.platform;
                // Sync re-pulls data using the stored credential; Reconnect
                // replaces the credential. Once the platform has rejected it,
                // Sync can only fail, so point the user at the one action that
                // can actually recover the account.
                const credentialRejected = !isManaged && account.tokenStatus === 'expired';

                return (
                  <motion.div
                    key={account.id}
                    custom={index}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    layout
                  >
                    <AppCard
                      className={clsx(styles.accountCard, isManaged && styles.accountCardManaged)}
                    >
                      <div className={styles.accountHeader}>
                        {isPlatformLogoAvailable(account.platform) ? (
                          <div className={clsx(styles.platformIcon, styles.platformIconLogo)}>
                            <PlatformLogo platform={account.platform} size={24} />
                          </div>
                        ) : (
                          <div
                            className={styles.platformIcon}
                            style={{
                              background: platformConfig?.color ?? 'var(--color-text-secondary)',
                            }}
                          >
                            {(platformConfig?.label ?? account.platform).charAt(0)}
                          </div>
                        )}
                        <div className={styles.accountInfo}>
                          <h3 className={styles.accountName}>{account.accountName}</h3>
                          <span className={styles.platformLabel}>
                            {platformConfig?.label ?? account.platform}
                          </span>
                        </div>
                        {isManaged ? (
                          <AppBadge variant="info" size="sm">
                            <Shield size={12} />
                            Managed by events.com
                          </AppBadge>
                        ) : (
                          <AppBadge variant={statusConfig.variant} size="sm">
                            {account.status === 'active' && <CheckCircle2 size={12} />}
                            {account.status === 'error' && <AlertCircle size={12} />}
                            {account.status === 'disconnected' && <XCircle size={12} />}
                            {statusConfig.label}
                          </AppBadge>
                        )}
                      </div>

                      <div className={styles.accountMeta}>
                        <div className={styles.metaItem}>
                          <span className={styles.metaLabel}>Account ID</span>
                          <span className={styles.metaValue}>{account.platformAccountId}</span>
                        </div>
                        {!isManaged && (
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Last Synced</span>
                            <span className={styles.metaValue}>
                              {formatDate(account.lastSyncedAt)}
                            </span>
                          </div>
                        )}
                        {isManaged && (
                          <div className={styles.metaItem}>
                            <span className={styles.metaLabel}>Type</span>
                            <span className={styles.metaValue}>events.com Managed</span>
                          </div>
                        )}
                      </div>

                      {mustReconnect && (
                        <div
                          className={clsx(
                            styles.reconnectBanner,
                            account.tokenStatus === 'expired'
                              ? styles.reconnectBannerExpired
                              : styles.reconnectBannerExpiring,
                          )}
                        >
                          <span className={styles.reconnectText}>
                            <AlertTriangle size={14} />
                            {account.tokenStatus === 'expired'
                              ? 'Connection expired. Reconnect to keep this account working.'
                              : missingPermission
                                ? 'This connection needs a new permission. Reconnect to enable boosting your Page posts.'
                                : 'Connection expiring soon. Reconnect to avoid disruption.'}
                          </span>
                          <AppButton
                            variant="secondary"
                            size="sm"
                            onClick={() => startConnect(account.platform)}
                            loading={isReconnecting}
                            disabled={isConnecting && !isReconnecting}
                          >
                            <RefreshCw size={14} />
                            Reconnect
                          </AppButton>
                        </div>
                      )}

                      {!isManaged && (
                        <div className={styles.accountActions}>
                          <AppButton
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSync(account)}
                            loading={isSyncing}
                            disabled={account.status === 'disconnected' || credentialRejected}
                            title={
                              credentialRejected
                                ? 'This account\u2019s connection was rejected by the platform. Reconnect it first.'
                                : undefined
                            }
                          >
                            <RefreshCw size={14} />
                            Sync
                          </AppButton>
                          <AppButton
                            variant="ghost"
                            size="sm"
                            onClick={() => setAccountToDisconnect(account)}
                            loading={isDisconnecting}
                            className={styles.disconnectButton}
                          >
                            <Unlink size={14} />
                            Disconnect
                          </AppButton>
                        </div>
                      )}
                    </AppCard>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Connect Account Dialog */}
        <AppDialog
          open={connectDialogOpen}
          onOpenChange={(open) => (open ? setConnectDialogOpen(true) : closeConnectDialog())}
          title="Connect Ad Account"
          description={
            isConnecting
              ? 'Finish authorizing in the tab that just opened. This page updates automatically.'
              : 'Select a platform to connect your advertising account via OAuth.'
          }
        >
          <div className={styles.connectDialogContent}>
            {isConnecting ? (
              <>
                <div className={styles.connectWaiting}>
                  <LoadingSpinner
                    size="md"
                    label={`Waiting for ${connectingLabel} authorization`}
                  />
                  <p className={styles.connectWaitingText}>
                    Waiting for <strong>{connectingLabel}</strong> authorization...
                  </p>
                  <p className={styles.connectWaitingHint}>
                    Do not close this page. If the new tab did not open, check your browser&apos;s
                    pop-up settings.
                  </p>
                </div>
                <div className={styles.connectDialogActions}>
                  <AppButton variant="secondary" onClick={cancelConnect}>
                    Cancel
                  </AppButton>
                </div>
              </>
            ) : (
              <>
                <AppSelect
                  label="Platform"
                  placeholder="Select a platform..."
                  options={platformOptions}
                  value={selectedPlatform}
                  onValueChange={setSelectedPlatform}
                />
                <div className={styles.connectDialogActions}>
                  <AppButton variant="secondary" onClick={closeConnectDialog}>
                    Cancel
                  </AppButton>
                  <AppButton onClick={handleConnectAccount} disabled={!selectedPlatform}>
                    {isPlatformLogoAvailable(selectedPlatform) ? (
                      <PlatformLogo platform={selectedPlatform} size={16} />
                    ) : (
                      <ExternalLink size={14} />
                    )}
                    Connect with {PLATFORM_CONFIG[selectedPlatform]?.label ?? 'Platform'}
                  </AppButton>
                </div>
              </>
            )}
          </div>
        </AppDialog>

        {/* Disconnect Confirmation Dialog */}
        <AppDialog
          open={accountToDisconnect !== null}
          onOpenChange={(open) => {
            if (!open && !disconnectMutation.isPending) setAccountToDisconnect(null);
          }}
          title="Disconnect ad account?"
          description="Campaigns using this account stop delivering until you reconnect it."
        >
          <div className={styles.confirmDialogContent}>
            <p className={styles.confirmText}>
              {accountToDisconnect?.accountName} will be disconnected from{' '}
              {PLATFORM_CONFIG[accountToDisconnect?.platform ?? '']?.label ??
                accountToDisconnect?.platform}
              . You can reconnect it at any time.
            </p>
            <div className={styles.connectDialogActions}>
              <AppButton
                variant="secondary"
                onClick={() => setAccountToDisconnect(null)}
                disabled={disconnectMutation.isPending}
              >
                Cancel
              </AppButton>
              <AppButton
                variant="danger"
                onClick={handleConfirmDisconnect}
                loading={disconnectMutation.isPending}
              >
                <Unlink size={14} />
                Disconnect
              </AppButton>
            </div>
          </div>
        </AppDialog>
      </div>
    </PageContainer>
  );
}
